/**
 * Format a number with commas and fixed decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with commas (e.g., "1,234.56")
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a currency value with symbol, commas, and fixed decimal places
 * @param value - The number to format
 * @param symbol - Currency symbol (e.g., "$" or "C$")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(value: number, symbol: string = '$', decimals: number = 2): string {
  return `${symbol}${formatNumber(value, decimals)}`;
}

/**
 * Format a number with sign prefix (+/-), commas, and fixed decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with sign (e.g., "+1,234.56" or "-1,234.56")
 */
export function formatWithSign(value: number, decimals: number = 2): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${formatNumber(value, decimals)}`;
}

/**
 * Format a currency value with sign prefix, symbol, commas, and fixed decimal places
 * @param value - The number to format
 * @param symbol - Currency symbol (e.g., "$" or "C$")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string with sign (e.g., "+$1,234.56")
 */
export function formatCurrencyWithSign(value: number, symbol: string = '$', decimals: number = 2): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${symbol}${formatNumber(Math.abs(value), decimals)}`;
}
